import { getCookie, removeCookie, setCookie } from './cookie';
import { UsajConfig, UserObject } from '../interfaces';
import { pushAnalyticsID } from '../analytics';
import { clearAuthenticationAttribution } from '../attribution';

export const saveJobSessionKey = 'saveJobAfterSignIn';
export const setDefaultFilterSessionKey = "setDefaultFilterAfterSignIn";
const userObjectVersionCookieName = 'usaj-u';
const userObjectVersionStorageKey = 'userObjectVersion';

document.addEventListener('userObjectChanged', () => {
    showTalentNetworksIfAvailable();
});

function isHostUsajobs(value: string) {
    if (value === '') { return true; }
    let url = document.createElement('a');
    url.href = value;
    return url.hostname.lastIndexOf('www.usajobs.gov') > -1 || url.hostname.lastIndexOf('localhost') > -1;
}

function getUserObjectVersion(): string | null {
    try {
        return getCookie(userObjectVersionCookieName);
    }
    catch {
        return null;
    }
}

async function initUserObject(): Promise<boolean> {
    return fetch("/Account/RetrieveUserPreferences")
        .then(response => response.text())
        .then(data => {
            let userObject = <UserObject>JSON.parse(data);
            userObject.LastLoad = new Date().getTime();
            if (userObject.AssignedFeatures.length > 0) {
                setCookie('usaj-f', JSON.stringify(userObject.AssignedFeatures));
            }
            setUserObject(userObject);
            return true;
        })
        .catch(err => {
            console.debug('Error', err);
            return false;
        })
        .finally(() => pushAnalyticsID());
}

async function setUsajConfig(): Promise<boolean> {
    const url = '/Client/GetClientConfigAsJson';

    return fetch(url)
        .then(response => response.text())
        .then(data => {
            let usajConfig = JSON.parse(data);
            sessionStorage.setItem('usajConfig', usajConfig['value'] === undefined ? usajConfig : usajConfig['value']);
            return true;
        }).catch(err => {
            console.debug('Error', err);
            return false;
        });
}

export function getUsajConfig(): UsajConfig {
    return <UsajConfig>JSON.parse(sessionStorage.getItem('usajConfig') || '{}');
}

export function setUserObject(userObject: UserObject) {
    sessionStorage.setItem('userObject', JSON.stringify(userObject));
    dispatchEvent(new Event('userObjectChanged'));
}

export function getUserObject(): UserObject {
    return <UserObject>JSON.parse(sessionStorage.getItem('userObject') || '{}');
}

export function getCEResponsesData(): UserObject {
    return <UserObject>JSON.parse(sessionStorage.getItem('CEResponsesData') || '{}');
}

export function addSavedJobToSession(documentId: string) {
    let uo = getUserObject();
    uo.SavedJobs[documentId] = true;
}

export function removeSavedJobFromSession(documentId: string) {
    let uo = getUserObject();
    delete uo.SavedJobs[documentId];
}

export function isAuthenticated(): boolean {
    let uo = getUserObject();
    if (uo === null) { return false; }
    return uo.IsAuthenticated;
}

export function logout() {
    localStorage.setItem('usaj-l', 'false');
    removeCookie('usaj-cid');
    sessionStorage.removeItem('dismissedAlerts');
    sessionStorage.removeItem('usajConfig');
    sessionStorage.removeItem('userObject');
    clearAuthenticationAttribution();
    location.href = '/Account/Logoff';
}

export async function initSession() {
    if (sessionStorage.getItem('dismissedAlerts') === null) {
        sessionStorage.setItem('dismissedAlerts', '{}');
    }
    if (sessionStorage.getItem("xSource") === null) {
        sessionStorage.setItem("xSource", document.referrer);
    }
    let currentxSource = sessionStorage.getItem("xSource") || '';
    if (currentxSource !== document.referrer && isHostUsajobs(currentxSource) && !isHostUsajobs(document.referrer)) {
        sessionStorage.setItem("xSource", document.referrer);
    }
    const userObjectVersion = getUserObjectVersion();
    const userObjectVersionChanged = userObjectVersion !== null && userObjectVersion !== sessionStorage.getItem(userObjectVersionStorageKey);
    const shouldInitUserObject = sessionStorage.getItem("userObject") === null || sessionStorage.getItem('usajConfig') === null || userObjectVersionChanged;
    if (shouldInitUserObject) {
        window.localStorage.setItem("usaj-l", 'true');
        const userObjectLoaded = await initUserObject();
        const usajConfigLoaded = await setUsajConfig();
        if (userObjectLoaded && usajConfigLoaded && userObjectVersion !== null) {
            sessionStorage.setItem(userObjectVersionStorageKey, userObjectVersion);
        }
    }
    else {
        pushAnalyticsID();
    }

    window.addEventListener('storage', evt => {
        if (evt.key === "usaj-l" && evt.newValue === "false") { logout(); }
    }, false);

    document.dispatchEvent(new Event("session-loaded"));
}

export function showTalentNetworksIfAvailable() {
    const userObject = getUserObject();
    if (userObject && userObject.HasAvailableTalentNetworks) {
        const talentNetworks = document.querySelectorAll<HTMLLIElement>('.talent-networks');
        talentNetworks.forEach(tn => tn.classList.remove('hidden'));
    }
}
